import React from "react";

/* ─── Base shimmer block ─────────────────────────────────────────────────── */
export function Skeleton({ className = "", style }) {
  return (
    <div
      className={`skeleton-shimmer rounded ${className}`}
      style={style}
    />
  );
}

/* ─── Full-page / section skeletons ─────────────────────────────────────── */

/** Dashboard: 4 KPI cards + chart area */
export function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-9 w-36 rounded-lg" />
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      {/* charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Skeleton className="h-72 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
    </div>
  );
}

/** Generic table rows */
export function TableRowsSkeleton({ rows = 5, cols = 6 }) {
  return (
    <>
      {[...Array(rows)].map((_, r) => (
        <tr key={r} className="border-t border-border">
          {[...Array(cols)].map((_, c) => (
            <td key={c} className="py-3 px-4">
              <Skeleton className={`h-4 ${c === 0 ? "w-16" : c === cols - 1 ? "w-10" : "w-full"}`} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Product cards grid (CustomerOrder) */
export function ProductCardsSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="rounded-lg overflow-hidden border border-gray-100">
          <Skeleton className="w-full pt-[100%]" />
          <div className="p-2 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Settings page */
export function SettingsSkeleton() {
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white border border-gray-200 rounded-lg shadow-sm mt-4 space-y-5">
      <div className="flex items-center gap-3 border-b pb-4">
        <Skeleton className="h-6 w-6 rounded" />
        <div className="space-y-1.5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
      <Skeleton className="h-9 w-32 rounded-lg" />
    </div>
  );
}

/** Inline row (for Debts / CustomerCare expandable sections) */
export function InlineRowsSkeleton({ rows = 3 }) {
  return (
    <div className="space-y-2 py-3">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex gap-3 items-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

/** Finance transaction rows */
export function FinanceRowsSkeleton({ rows = 6 }) {
  return (
    <>
      {[...Array(rows)].map((_, r) => (
        <tr key={r} className="border-t border-border">
          <td className="py-3 px-4"><Skeleton className="h-4 w-20" /></td>
          <td className="py-3 px-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
          <td className="py-3 px-4"><Skeleton className="h-4 w-24" /></td>
          <td className="py-3 px-4"><Skeleton className="h-4 w-32" /></td>
          <td className="py-3 px-4 text-right"><Skeleton className="h-4 w-20 ml-auto" /></td>
          <td className="py-3 px-4 text-right"><Skeleton className="h-4 w-16 ml-auto" /></td>
          <td className="py-3 px-4"><Skeleton className="h-6 w-6 rounded ml-auto" /></td>
        </tr>
      ))}
    </>
  );
}

/** Chat typing indicator */
export function ChatTypingSkeleton() {
  return (
    <div className="flex items-end gap-2 justify-start">
      <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="block w-2 h-2 rounded-full bg-ink-muted/50 animate-bounce"
            style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.9s" }}
          />
        ))}
      </div>
    </div>
  );
}
